import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { notificationsService } from "@/lib/services";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, Users, CalendarCheck, Wallet, GraduationCap, Bell,
  Calendar, LogOut, ChevronDown, UserCog, Banknote, Lock, BookOpen, ClipboardList, Table2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { canAccess } from "@/lib/rbac";
import { toast } from "sonner";
import { useState } from "react";
import { SCHOOL } from "@/lib/school";
import { ChangePasswordDialog } from "./ChangePasswordDialog";

const NAV: { to: string; label: string; icon: React.ComponentType<any> }[] = [
  { to: "/dashboard",     label: "Dashboard",     icon: LayoutDashboard },
  { to: "/students",      label: "Students",      icon: Users },
  { to: "/master-data",   label: "Master Data",   icon: Table2 },
  { to: "/staff",         label: "Staff",         icon: UserCog },
  { to: "/users",         label: "Users",         icon: UserCog },
  { to: "/attendance",    label: "Attendance",    icon: CalendarCheck },
  { to: "/cashier",       label: "Collect Fees",  icon: Banknote },
  { to: "/fees",          label: "Fees",          icon: Wallet },
  { to: "/exams",         label: "Exams",         icon: GraduationCap },
  { to: "/class-tests",   label: "Class Tests",   icon: ClipboardList },
  { to: "/academics",     label: "Academics",     icon: BookOpen },
  { to: "/timetable",     label: "Timetable",     icon: Calendar },
  { to: "/notifications", label: "Notifications", icon: Bell },
];

const ROLE_COLOR: Record<string, string> = {
  admin: "bg-indigo-500", teacher: "bg-cyan-500", student: "bg-emerald-500",
  parent: "bg-amber-500", cashier: "bg-fuchsia-500", examhead: "bg-purple-500",
  principal: "bg-rose-500",
};

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const unreadQ = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: () => notificationsService.unreadCount(),
    refetchInterval: 45_000,
  });
  const [pwOpen, setPwOpen] = useState(false);

  if (!user) return null;

  const items = NAV.filter((n) => canAccess(user, n.to));
  const unread = unreadQ.data?.count || 0;

  const doLogout = () => {
    logout();
    toast.info("Signed out");
    navigate("/login", { replace: true });
  };

  const mustReset = !!user.must_reset_password;

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="flex w-64 flex-col border-r bg-white">
        <div className="flex h-16 items-center gap-3 border-b px-6">
          <img src={SCHOOL.logo} alt={SCHOOL.shortName} className="h-10 w-10 rounded-md object-contain" />
          <div className="min-w-0">
            <div className="truncate text-sm font-bold leading-tight text-emerald-700">{SCHOOL.shortName} ERP</div>
            <div className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">{SCHOOL.tagline}</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) => cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-slate-100",
              )}
            >
              <it.icon className="h-4 w-4" />
              <span className="flex-1">
                {it.to === "/students" && user.role === "parent" ? "Child Profile" : it.label}
              </span>
              {it.to === "/notifications" && unread > 0 && (
                <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t p-4">
          <div className="mb-3 flex items-center gap-3">
            <div className={cn("flex h-9 w-9 items-center justify-center rounded-full text-white text-sm font-bold", ROLE_COLOR[user.role])}>
              {user.name.slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{user.name}</div>
              <div className="truncate text-xs text-muted-foreground">{user.email}</div>
            </div>
            <Badge variant="outline" className="capitalize">{user.role}</Badge>
          </div>
          <Button variant="outline" size="sm" className="w-full mb-2" onClick={() => setPwOpen(true)}>
            <Lock className="mr-2 h-4 w-4" /> Change password
          </Button>
          <Button variant="outline" size="sm" className="w-full" onClick={doLogout}>
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex flex-1 flex-col overflow-hidden">
        <Topbar />
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl p-6 lg:p-10">
            <Outlet />
          </div>
        </div>
      </main>

      {(pwOpen || mustReset) && (
        <ChangePasswordDialog
          forced={mustReset}
          onClose={() => setPwOpen(false)}
        />
      )}
    </div>
  );
}

function Topbar() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: years = [] } = useQuery({
    queryKey: ["academic-years"],
    queryFn: () => api.academicYears.list(),
  });
  const { data: current } = useQuery({
    queryKey: ["academic-years", "current"],
    queryFn: () => api.academicYears.current().catch(() => null),
  });
  const setCurrent = useMutation({
    mutationFn: (id: string) => api.academicYears.setCurrent(id),
    onSuccess: () => {
      toast.success("Academic year switched");
      qc.invalidateQueries();
      setOpen(false);
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (!user) return null;
  return (
    <div className="flex h-14 items-center justify-between border-b bg-white px-6">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">
        {new Date().toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
      </div>
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(!open)}
          className="gap-2"
        >
          <Calendar className="h-4 w-4 text-primary" />
          <span className="font-semibold">AY {current?.name || "—"}</span>
          {(user.role === "admin" || user.role === "principal") && <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </Button>
        {open && (user.role === "admin" || user.role === "principal") && (
          <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-md border bg-white p-1 shadow-lg">
            {years.map((y: any) => (
              <button
                key={y.id}
                onClick={() => setCurrent.mutate(y.id)}
                disabled={setCurrent.isPending || y.is_current}
                className={cn(
                  "flex w-full items-center justify-between rounded-sm px-3 py-2 text-left text-sm hover:bg-accent",
                  y.is_current && "bg-primary/10 text-primary font-semibold",
                )}
              >
                <span>{y.name}</span>
                <span className="text-xs text-muted-foreground">
                  {y.start_date.slice(0, 7)} → {y.end_date.slice(0, 7)}
                </span>
              </button>
            ))}
            <div className="border-t my-1" />
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Switch to retrieve data for a different academic year.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
